/**
 * Campaigns API Service
 * Handles all campaign-related API calls with role-based routing
 * 
 * Users: /api/v1/campaigns
 * Admins: /api/v1/administrator/campaigns
 */

import { apiClient } from './client'
import { Campaign } from '../../types'
import { getStoredRole } from '../../utils/token'

/**
 * Pagination metadata from API response
 */
export interface PaginationMeta {
  current_page: number
  total_pages: number
  total_count: number
}

/**
 * Campaigns list response with pagination
 */
interface CampaignsListResponse {
  campaigns: Campaign[]
  meta: PaginationMeta
}

/**
 * Single campaign response
 */
interface CampaignResponse {
  campaign: Campaign
}

/**
 * Delete campaign response
 */
interface DeleteCampaignResponse {
  message: string
}

/**
 * Query parameters for listing campaigns
 */
export interface CampaignsQueryParams {
  q?: string                                    // Search query
  search_by?: 'Campaigns' | 'Advertisers' | 'Agencies'  // Search type
  page?: number                                 // Page number (default: 1)
  advertiser_id?: number                        // Filter by advertiser ID
}

/**
 * Request body for creating a campaign
 */
export interface CreateCampaignRequest {
  name: string                    // Required: Campaign name (max 128 chars)
  description?: string            // Optional: Campaign description (max 1000 chars)
  advertiser_id?: number          // Optional: Advertiser ID
}

/**
 * Request body for updating a campaign
 */
export interface UpdateCampaignRequest {
  name?: string
  description?: string
  active?: boolean
  report_delivery_expiration_date?: string | null
  report_delivery_emails?: string[]
}

/**
 * Get the base endpoint path based on current user role
 * Note: VITE_API_BASE_URL already includes /api/v1
 */
function getCampaignsBasePath(): string {
  const role = getStoredRole()
  return role === 'admin' 
    ? '/administrator/campaigns' 
    : '/campaigns'
}

/**
 * Build query string from params
 */
function buildQueryString(params: CampaignsQueryParams): string {
  const searchParams = new URLSearchParams()

  if (params.q) {
    searchParams.append('q', params.q)
  }
  if (params.search_by) {
    searchParams.append('search_by', params.search_by)
  }
  if (params.page && params.page > 1) {
    searchParams.append('page', params.page.toString())
  }
  if (params.advertiser_id) {
    searchParams.append('advertiser_id', params.advertiser_id.toString())
  }

  const queryString = searchParams.toString()
  return queryString ? `?${queryString}` : ''
}

export const campaignApi = {
  /**
   * Get paginated list of campaigns with optional search
   * 
   * @param params - Query parameters for filtering/pagination
   * @returns Campaigns array and pagination metadata
   */
  getCampaigns: async (params: CampaignsQueryParams = {}): Promise<{
    campaigns: Campaign[]
    meta: PaginationMeta
  }> => {
    const basePath = getCampaignsBasePath()
    const queryString = buildQueryString(params)
    
    const response = await apiClient.get<CampaignsListResponse>(
      `${basePath}${queryString}`,
      { skipRoleRouting: true }
    )
    
    return {
      campaigns: response.campaigns || [],
      meta: response.meta || { current_page: 1, total_pages: 1, total_count: 0 }
    }
  },

  /**
   * Get a specific campaign by ID
   * 
   * @param id - Campaign ID
   * @returns Campaign details with nested advertiser/agency
   */
  getCampaign: async (id: number): Promise<Campaign> => {
    const basePath = getCampaignsBasePath()
    
    const response = await apiClient.get<CampaignResponse>(
      `${basePath}/${id}`,
      { skipRoleRouting: true }
    )
    
    return response.campaign
  },

  /**
   * Create a new campaign
   * 
   * @param data - Campaign creation data
   * @returns Created campaign
   */
  createCampaign: async (data: CreateCampaignRequest): Promise<Campaign> => {
    const basePath = getCampaignsBasePath()
    
    const response = await apiClient.post<CampaignResponse>(
      basePath,
      { campaign: data },
      { skipRoleRouting: true }
    )
    
    return response.campaign
  },

  /**
   * Update an existing campaign
   * 
   * @param id - Campaign ID
   * @param data - Campaign update data
   * @returns Updated campaign
   */
  updateCampaign: async (id: number, data: UpdateCampaignRequest): Promise<Campaign> => {
    const basePath = getCampaignsBasePath()
    
    const response = await apiClient.patch<CampaignResponse>(
      `${basePath}/${id}`,
      { campaign: data },
      { skipRoleRouting: true }
    )
    
    return response.campaign
  },

  /**
   * Delete a campaign (soft delete - sets active: false)
   * 
   * @param id - Campaign ID
   * @returns Success message
   */
  deleteCampaign: async (id: number): Promise<string> => {
    const basePath = getCampaignsBasePath()
    
    const response = await apiClient.delete<DeleteCampaignResponse>(
      `${basePath}/${id}`,
      { skipRoleRouting: true }
    )
    
    return response.message
  },
}
